import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { clearAllFilterSnapshots } from '@/utils/filterSnapshot';

export type StaffRole =
  | 'owner'
  | 'manager'
  | 'cashier'
  | 'inventory'
  | 'viewer'
  | 'b2b_sales'
   | 'b2b_sales_manager'
   | 'accountant'
   | 'employee_self_service'
   // TCR-1521 — app của người giao hàng. Danh sách này vẫn còn thiếu 7 vai trò khác
   // đã có ở backend (nv_kho, ke_toan_truong, gd_hr…); dọn hết là một việc riêng.
   | 'shipper';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatar?: string;
  /**
   * Primary role alias — derived từ roleIds[0] khi nhiều role. Giữ để tương thích Sidebar label hiển thị.
   * Source of truth cho RBAC là `roleIds` + `useRbac().hasPermission()`.
   */
  role: StaffRole;
  /** TCR-2075 — mọi vai trò (chính đứng đầu) từ /auth/me. Phiên cũ có thể thiếu → dùng `userRolesOf()`. */
  roles?: string[];
  roleIds: string[];
  /** Authorization source of truth returned by GET /auth/me. Undefined until hydrated. */
  permissions?: string[];
  tenantId: string;
  storeId?: string;
  /** Array of store_id strings — populated from user.store_assignments at login. */
  storeAssignments: string[];
  /** User's default store (User.delivery_store_id). Fallback cho owner chưa có store_assignments. */
  deliveryStoreId?: string;
  /** Tenant-level locale context returned by GET /auth/me. */
  locale?: 'vi' | 'en';
  timezone?: string;
  currency?: 'VND' | 'USD';
  dateFormat?: string;
}


interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
}

const storedAccessToken = typeof window !== 'undefined'
  ? window.localStorage?.getItem('erp_access_token') ?? null
  : null;
const storedRefreshToken = typeof window !== 'undefined'
  ? window.localStorage?.getItem('erp_refresh_token') ?? null
  : null;

const initialState: AuthState = {
  user: null,
  accessToken: storedAccessToken,
  refreshToken: storedRefreshToken,
  isAuthenticated: Boolean(storedAccessToken),
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{ accessToken: string; refreshToken: string; user: AuthUser }>
    ) => {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.user = action.payload.user;
      state.isAuthenticated = true;
      localStorage.setItem('erp_access_token', action.payload.accessToken);
      localStorage.setItem('erp_refresh_token', action.payload.refreshToken);
    },
    syncRefreshedCredentials: (
      state,
      action: PayloadAction<{ accessToken: string; refreshToken?: string }>,
    ) => {
      state.accessToken = action.payload.accessToken;
      state.isAuthenticated = true;
      localStorage.setItem('erp_access_token', action.payload.accessToken);
      if (action.payload.refreshToken) {
        state.refreshToken = action.payload.refreshToken;
        localStorage.setItem('erp_refresh_token', action.payload.refreshToken);
      }
    },
    clearCredentials: (state) => {
      state.accessToken = null;
      state.refreshToken = null;
      state.user = null;
      state.isAuthenticated = false;
      localStorage.removeItem('erp_access_token');
      localStorage.removeItem('erp_refresh_token');
      localStorage.removeItem('erp_access_token_expires_at');
      // Single source-of-truth khi kết thúc phiên (logout / token hết hạn /
      // 401 refresh fail): xoá POS shift binding để tránh stale cross-tenant
      // store_id bị interceptor inject sang phiên mới (→ 403 STORE_ACCESS_DENIED).
      try {
        sessionStorage.removeItem('pos.shiftStoreId');
        sessionStorage.removeItem('pos.shiftStoreTenantId');
      } catch {
        /* sessionStorage unavailable (SSR / private mode) */
      }
      // Snapshot filter được giữ trong localStorage để sống qua F5 / đóng-mở tab,
      // nhưng `search` chứa SĐT/tên khách nên không được nằm lại sau khi hết phiên
      // trên máy POS dùng chung. Cột (`erp_col_*`) là preference giao diện thuần,
      // không chứa dữ liệu khách nên giữ nguyên.
      clearAllFilterSnapshots();
    },
    setUser: (state, action: PayloadAction<AuthUser>) => {
      state.user = action.payload;
      const accessToken = state.accessToken || localStorage.getItem('erp_access_token');
      if (accessToken) {
        state.accessToken = accessToken;
        state.refreshToken = state.refreshToken || localStorage.getItem('erp_refresh_token');
        state.isAuthenticated = true;
      }
    },
  },
});

export const { setCredentials, syncRefreshedCredentials, clearCredentials, setUser } =
  authSlice.actions;
export default authSlice.reducer;
